import React, { CSSProperties, KeyboardEvent, ReactNode, useEffect, useMemo, useState } from "react";

import "./AppDataTable_New.scss";

export type AppDataTableTone = "neutral" | "info" | "warning" | "danger" | "success";

export interface AppDataTableColumn<T> {
    key: string;
    header: ReactNode;
    render: (row: T) => ReactNode;
    getSearchValue?: (row: T) => unknown;
    width?: CSSProperties["width"];
    align?: "left" | "center" | "right";
    className?: string;
}

export interface AppDataTableFilter<T> {
    id: string;
    label: ReactNode;
    predicate: (row: T) => boolean;
    tone?: AppDataTableTone;
}

export interface AppDataTableActivityItem {
    label: ReactNode;
    value: ReactNode;
}

export interface AppDataTableActivity<T> {
    header?: ReactNode;
    buttonLabel?: ReactNode | ((row: T) => ReactNode);
    getItems: (row: T) => AppDataTableActivityItem[];
}

export interface AppDataTable_NewProps<T> {
    rows: T[];
    columns: AppDataTableColumn<T>[];
    getRowId: (row: T) => React.Key;
    title?: ReactNode;
    searchPlaceholder?: string;
    searchAriaLabel?: string;
    getSearchText?: (row: T) => string;
    filters?: AppDataTableFilter<T>[];
    filterLabel?: ReactNode;
    activity?: AppDataTableActivity<T>;
    emptyMessage?: ReactNode;
    pageSize?: number;
    onRowClick?: (row: T) => void;
    className?: string;
}

export interface AppDataTableBadgeProps {
    children: ReactNode;
    tone?: AppDataTableTone;
    className?: string;
}

export const AppDataTableBadge: React.FC<AppDataTableBadgeProps> = ({
    children,
    tone = "neutral",
    className = "",
}) => (
    <span className={`app-data-table__badge app-data-table__badge--${tone} ${className}`.trim()}>
        {children}
    </span>
);

const SearchIcon = () => (
    <svg viewBox="0 0 24 24" aria-hidden="true">
        <circle cx="11" cy="11" r="7" />
        <path d="m16 16 5 5" />
    </svg>
);

const ClockIcon = () => (
    <svg viewBox="0 0 24 24" aria-hidden="true">
        <circle cx="12" cy="12" r="8" />
        <path d="M12 7v5l3 2" />
    </svg>
);

function AppDataTable_New<T>({
    rows,
    columns,
    getRowId,
    title,
    searchPlaceholder = "Search by Keywords",
    searchAriaLabel = "Search table",
    getSearchText,
    filters = [],
    filterLabel = "Filter by:",
    activity,
    emptyMessage = "No results found.",
    pageSize = 10,
    onRowClick,
    className = "",
}: AppDataTable_NewProps<T>) {
    const [keyword, setKeyword] = useState("");
    const [activeFilterId, setActiveFilterId] = useState<string | null>(null);
    const [page, setPage] = useState(1);
    const [openActivityId, setOpenActivityId] = useState<React.Key | null>(null);

    const safePageSize = Math.max(1, pageSize);
    const normalizedKeyword = keyword.trim().toLocaleLowerCase();

    const filterCounts = useMemo(
        () => new Map(filters.map((filter) => [filter.id, rows.filter(filter.predicate).length])),
        [filters, rows]
    );

    const filteredRows = useMemo(() => {
        const activeFilter = filters.find((filter) => filter.id === activeFilterId);

        return rows.filter((row) => {
            const text = getSearchText
                ? getSearchText(row)
                : columns
                      .map((column) => column.getSearchValue?.(row) ?? "")
                      .join(" ");
            const matchesSearch =
                !normalizedKeyword || String(text).toLocaleLowerCase().includes(normalizedKeyword);
            return matchesSearch && (!activeFilter || activeFilter.predicate(row));
        });
    }, [activeFilterId, columns, filters, getSearchText, normalizedKeyword, rows]);

    const pageCount = Math.max(1, Math.ceil(filteredRows.length / safePageSize));
    const currentPage = Math.min(page, pageCount);
    const startIndex = (currentPage - 1) * safePageSize;
    const visibleRows = filteredRows.slice(startIndex, startIndex + safePageSize);

    useEffect(() => {
        setPage(1);
        setOpenActivityId(null);
    }, [keyword, activeFilterId]);

    useEffect(() => {
        if (page > pageCount) setPage(pageCount);
    }, [page, pageCount]);

    const activateRow = (event: KeyboardEvent<HTMLTableRowElement>, row: T) => {
        if (!onRowClick || (event.key !== "Enter" && event.key !== " ")) return;
        event.preventDefault();
        onRowClick(row);
    };

    return (
        <section className={`app-data-table ${className}`.trim()}>
            <div className="app-data-table__toolbar">
                {title && <h2 className="app-data-table__title">{title}</h2>}

                <div className="app-data-table__controls">
                    <label className="app-data-table__search">
                        <span className="app-data-table__search-icon"><SearchIcon /></span>
                        <span className="visually-hidden">{searchAriaLabel}</span>
                        <input
                            type="search"
                            value={keyword}
                            placeholder={searchPlaceholder}
                            onChange={(event) => setKeyword(event.target.value)}
                        />
                    </label>

                    {filters.length > 0 && (
                        <div className="app-data-table__filters" aria-label="Table filters">
                            <span className="app-data-table__filter-label">{filterLabel}</span>
                            {filters.map((filter) => {
                                const selected = activeFilterId === filter.id;
                                const tone = filter.tone ?? "neutral";
                                return (
                                    <button
                                        key={filter.id}
                                        type="button"
                                        className={`app-data-table__filter app-data-table__filter--${tone}${selected ? " is-active" : ""}`}
                                        aria-pressed={selected}
                                        onClick={() => setActiveFilterId(selected ? null : filter.id)}
                                    >
                                        <span className="app-data-table__filter-dot" />
                                        <span>{filter.label}</span>
                                        <strong>{filterCounts.get(filter.id) ?? 0}</strong>
                                    </button>
                                );
                            })}
                        </div>
                    )}
                </div>
            </div>

            <div className="app-data-table__frame">
                <div className="app-data-table__scroll">
                    <table>
                        <thead>
                            <tr>
                                {columns.map((column) => (
                                    <th
                                        key={column.key}
                                        scope="col"
                                        className={`${column.className ?? ""} ${column.align ? `is-${column.align}` : ""}`.trim()}
                                        style={{ width: column.width }}
                                    >
                                        {column.header}
                                    </th>
                                ))}
                                {activity && <th scope="col">{activity.header ?? "Activity"}</th>}
                            </tr>
                        </thead>
                        <tbody>
                            {visibleRows.map((row) => {
                                const rowId = getRowId(row);
                                const activityOpen = openActivityId === rowId;
                                return (
                                    <tr
                                        key={rowId}
                                        className={onRowClick ? "is-clickable" : undefined}
                                        tabIndex={onRowClick ? 0 : undefined}
                                        onClick={() => onRowClick?.(row)}
                                        onKeyDown={(event) => activateRow(event, row)}
                                    >
                                        {columns.map((column) => (
                                            <td
                                                key={column.key}
                                                className={`${column.className ?? ""} ${column.align ? `is-${column.align}` : ""}`.trim()}
                                            >
                                                {column.render(row)}
                                            </td>
                                        ))}
                                        {activity && (
                                            <td className="app-data-table__activity-cell">
                                                <button
                                                    type="button"
                                                    className="app-data-table__activity-button"
                                                    aria-expanded={activityOpen}
                                                    onClick={(event) => {
                                                        event.stopPropagation();
                                                        setOpenActivityId(activityOpen ? null : rowId);
                                                    }}
                                                >
                                                    <ClockIcon />
                                                    {typeof activity.buttonLabel === "function"
                                                        ? activity.buttonLabel(row)
                                                        : activity.buttonLabel ?? "Activity"}
                                                </button>

                                                {activityOpen && (
                                                    <div className="app-data-table__activity-popover" onClick={(event) => event.stopPropagation()}>
                                                        {activity.getItems(row).map((item, index) => (
                                                            <div className="app-data-table__activity-item" key={index}>
                                                                <span>{item.label}</span>
                                                                <strong>{item.value || "-"}</strong>
                                                            </div>
                                                        ))}
                                                    </div>
                                                )}
                                            </td>
                                        )}
                                    </tr>
                                );
                            })}

                            {visibleRows.length === 0 && (
                                <tr>
                                    <td className="app-data-table__empty" colSpan={columns.length + (activity ? 1 : 0)}>
                                        {emptyMessage}
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>

                <footer className="app-data-table__footer">
                    <span>
                        {filteredRows.length === 0
                            ? "Showing 0 results"
                            : `Showing ${startIndex + 1}-${Math.min(startIndex + safePageSize, filteredRows.length)} of ${filteredRows.length}`}
                    </span>
                    <div className="app-data-table__pagination" aria-label="Table pagination">
                        <button type="button" disabled={currentPage === 1} onClick={() => setPage(currentPage - 1)} aria-label="Previous page">‹</button>
                        <span>Page {currentPage} of {pageCount}</span>
                        <button type="button" disabled={currentPage === pageCount} onClick={() => setPage(currentPage + 1)} aria-label="Next page">›</button>
                    </div>
                </footer>
            </div>
        </section>
    );
}

export default AppDataTable_New;
